import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Video,
  MessageSquare,
  Users,
  Shield,
  Monitor,
  Cloud,
  Lock,
  Zap,
  Check,
  Mic,
  Camera,
  BarChart3,
  Globe,
  Hand,
  FileText,
  Calendar,
  QrCode,
  Smile,
  UserCheck,
  Settings,
  Layers,
  PenTool,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";

export default function Features() {
  const navigate = useNavigate();

  const coreFeatures = [
    {
      icon: Video,
      title: "HD Video Conferencing",
      description: "Crystal-clear video with adjustable quality settings (low/medium/high) to optimize for your connection speed."
    },
    {
      icon: Mic,
      title: "Professional Audio",
      description: "Echo cancellation, noise suppression, and auto gain control for clear audio in any environment."
    },
    {
      icon: Monitor,
      title: "Screen Sharing",
      description: "Share your entire screen or specific windows with real-time viewing for all participants."
    },
    {
      icon: MessageSquare,
      title: "Real-Time Chat",
      description: "Instant messaging during meetings with message history that persists throughout the session."
    },
    {
      icon: Users,
      title: "Breakout Rooms",
      description: "Split participants into smaller groups for focused discussions, workshops, or team activities."
    },
    {
      icon: Lock,
      title: "Waiting Room",
      description: "Control who joins your meeting with host approval. Admit participants individually or all at once."
    }
  ];

  const advancedFeatures = [
    {
      icon: Camera,
      title: "Virtual Backgrounds",
      description: "Apply blur effects, preset backgrounds, or upload custom images for a professional appearance."
    },
    {
      icon: FileText,
      title: "Live Captions",
      description: "Real-time automatic speech-to-text captioning using browser's Web Speech API for accessibility."
    },
    {
      icon: Hand,
      title: "Webinar Mode",
      description: "Host webinars with raise hand feature, allowing attendees to request speaking permissions."
    },
    {
      icon: Layers,
      title: "Polls & Q&A",
      description: "Create interactive polls with real-time voting and Q&A sessions with upvoting support."
    },
    {
      icon: Cloud,
      title: "Meeting Recording",
      description: "Record meetings with playback, download options, and storage management for hosts."
    },
    {
      icon: Globe,
      title: "Meeting Transcription",
      description: "Full meeting transcripts with speaker labels and timestamps generated from live captions."
    }
  ];

  const productivityFeatures = [
    {
      icon: Calendar,
      title: "Calendar Integration",
      description: "Sync with Google Calendar, Outlook, and export to ICS files for seamless scheduling."
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Track meeting duration, participant counts, message activity, and engagement metrics."
    },
    {
      icon: QrCode,
      title: "QR Code Sharing",
      description: "Generate QR codes for easy meeting sharing - participants can join by scanning."
    },
    {
      icon: Smile,
      title: "Reactions & Emojis",
      description: "Express yourself with floating emoji reactions visible to all participants."
    },
    {
      icon: PenTool,
      title: "Screen Annotation",
      description: "Draw, highlight, and annotate on shared screens for interactive presentations."
    },
    {
      icon: UserCheck,
      title: "Guest Access",
      description: "Join meetings without creating an account - perfect for external participants."
    }
  ];

  const securityFeatures = [
    "Password-protected meetings",
    "Waiting room with host approval",
    "Role-based access control",
    "Admin & moderator roles",
    "Meeting host controls",
    "Secure participant management"
  ];

  const hostControls = [
    {
      icon: Settings,
      title: "Admin Panel",
      description: "Comprehensive dashboard for managing users, roles, meetings, and platform settings."
    },
    {
      icon: Users,
      title: "Participant Management",
      description: "Mute/unmute participants, remove users, and control meeting access in real-time."
    },
    {
      icon: Hand,
      title: "Raise Hand Queue",
      description: "Manage speaker requests in webinar mode with invite to speak or dismiss options."
    },
    {
      icon: Lock,
      title: "Meeting Controls",
      description: "Enable/disable waiting room, recording, and webinar mode per meeting."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Navigation />
      <div className="container mx-auto px-4 py-8 pt-24">

        <div className="max-w-6xl mx-auto space-y-12">
          {/* Hero Section */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center mb-4">
              <Zap className="h-12 w-12 text-primary mr-3" />
              <h1 className="text-4xl font-bold">MeetHub Features</h1>
            </div>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need for professional video meetings, webinars, and team collaboration
            </p>
          </div>

          {/* Core Features */}
          <section>
            <h2 className="text-3xl font-bold text-center mb-8">Core Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {coreFeatures.map((feature, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <feature.icon className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Advanced Features */}
          <section>
            <h2 className="text-3xl font-bold text-center mb-8">Advanced Capabilities</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {advancedFeatures.map((feature, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <feature.icon className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Productivity Features */}
          <section>
            <h2 className="text-3xl font-bold text-center mb-8">Productivity Tools</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {productivityFeatures.map((feature, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <feature.icon className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Host Controls */}
          <section>
            <h2 className="text-3xl font-bold text-center mb-8">Host & Admin Controls</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {hostControls.map((feature, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <feature.icon className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </section>

          {/* Security Section */}
          <section>
            <Card className="bg-gradient-to-br from-primary/5 to-secondary/5">
              <CardHeader className="text-center">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle className="text-2xl">Security & Privacy</CardTitle>
                <CardDescription>Your meetings are protected with multiple layers of security</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                  {securityFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* CTA Section */}
          <section className="text-center space-y-6 py-8">
            <h2 className="text-3xl font-bold">Ready to Get Started?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Create your free account and start hosting professional video meetings today
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => navigate("/auth")}>
                Start Free Meeting
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/pricing")}>
                View Pricing
              </Button>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
